import { type FormEvent, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Zap, PlayCircle, Link2, Type, Mic, MicOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert } from "@/components/ui/alert";
import { ApiError, createTextVideo, getConfig } from "@/lib/api";
import { parseYouTubeId } from "@/lib/url";
import { countWords } from "@lib/format";
import { useSpeechToText } from "@/lib/speech";
import { LEVEL_NAMES } from "@/lib/levels";
import type { QuizCounts } from "@lib/domain";

const DEFAULT_COUNTS: QuizCounts = { 1: 5, 2: 8, 3: 6 };

interface TextLimits {
  min: number;
  maxFree: number;
  maxPaid: number;
  maxAccepted: number;
}

function countsParam(counts: QuizCounts): string {
  return `${counts[1]},${counts[2]},${counts[3]}`;
}

export function HomePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const prefilled = params.get("url") ?? "";
  const [tab, setTab] = useState<"youtube" | "text">("youtube");
  const [url, setUrl] = useState(prefilled);
  const [text, setText] = useState("");
  const [interim, setInterim] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [textError, setTextError] = useState<string | null>(null);
  const [textBusy, setTextBusy] = useState(false);
  const [counts, setCounts] = useState<QuizCounts>(DEFAULT_COUNTS);
  const [min, setMin] = useState(5);
  const [max, setMax] = useState(20);
  const [textLimits, setTextLimits] = useState<TextLimits>({
    min: 50,
    maxFree: 2000,
    maxPaid: 10000,
    maxAccepted: 100000,
  });

  const words = useMemo(() => countWords(text), [text]);
  const speech = useSpeechToText({
    onFinal: (phrase) => {
      setInterim("");
      setText((prev) => (prev ? `${prev.trimEnd()} ${phrase}` : phrase));
    },
    onInterim: setInterim,
    onError: (message) => setTextError(message),
  });

  useEffect(() => {
    let cancelled = false;
    getConfig()
      .then((config) => {
        if (cancelled) return;
        setMin(config.minQuestionsPerLevel);
        setMax(config.maxQuestionsPerLevel);
        setTextLimits({
          min: config.minTextWords,
          maxFree: config.maxTextWordsFree,
          maxPaid: config.maxTextWordsPaid,
          maxAccepted: config.maxTextWordsAccepted,
        });
        setCounts({
          1: config.levels[0]?.questionCount ?? config.questionCount ?? 5,
          2: config.levels[1]?.questionCount ?? 8,
          3: config.levels[2]?.questionCount ?? 6,
        });
      })
      .catch(() => {
        /* keep defaults when /config is unavailable */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const setCount = (level: keyof QuizCounts, value: string) => {
    const n = Number.parseInt(value, 10);
    setCounts((prev) => ({ ...prev, [level]: Number.isFinite(n) ? n : prev[level] }));
  };

  const clampCount = (level: keyof QuizCounts) => {
    setCounts((prev) => ({ ...prev, [level]: Math.min(max, Math.max(min, prev[level])) }));
  };

  const submit = (e: FormEvent, nextMode: "quiz-only" | "watch") => {
    e.preventDefault();
    const id = parseYouTubeId(url);
    if (!id) {
      setError("That doesn't look like a YouTube link. Paste a full video URL or a 11-char video ID.");
      return;
    }
    setError(null);
    const query = `?url=${encodeURIComponent(url.trim())}&counts=${countsParam(counts)}`;
    navigate(nextMode === "watch" ? `/watch/${id}${query}` : `/quiz/${id}${query}`);
  };

  const submitText = async (e: FormEvent) => {
    e.preventDefault();
    if (words < textLimits.min) {
      setTextError(`Add at least ${textLimits.min} words so there is enough material to quiz.`);
      return;
    }
    if (words > textLimits.maxAccepted) {
      setTextError(
        `That is ${words} words; the maximum we accept is ${textLimits.maxAccepted.toLocaleString()}. Trim it down and try again.`,
      );
      return;
    }
    setTextError(null);
    setTextBusy(true);
    try {
      const summary = await createTextVideo(text, counts);
      navigate(`/quiz/${summary.videoId}?counts=${countsParam(counts)}&mode=text`);
    } catch (err) {
      setTextError(err instanceof ApiError ? err.message : "Could not start the quiz. Try again.");
    } finally {
      setTextBusy(false);
    }
  };

  const overBudget = words > textLimits.maxFree;
  const overAccepted = words > textLimits.maxAccepted;

  const countInput = (level: 1 | 2 | 3) => (
    <div key={level} className="space-y-1">
      <Label htmlFor={`count-${level}`} className="text-xs">
        Level {level} · {LEVEL_NAMES[level]}
      </Label>
      <div className="flex items-center gap-2">
        <Input
          id={`count-${level}`}
          type="number"
          min={min}
          max={max}
          value={counts[level]}
          onChange={(e) => setCount(level, e.target.value)}
          onBlur={() => clampCount(level)}
          className="w-20"
        />
        <span className="text-xs text-muted-foreground">questions</span>
      </div>
    </div>
  );

  const countsBlock = (
    <div className="rounded-lg border p-3">
      <p className="text-sm font-medium">Questions per level</p>
      <p className="mb-3 text-xs text-muted-foreground">
        Pick how many questions each level should ask ({min}–{max}).
      </p>
      <div className="grid gap-3 sm:grid-cols-3">{([1, 2, 3] as const).map(countInput)}</div>
    </div>
  );

  return (
    <div className="flex flex-col items-center gap-6 py-8">
      <div className="text-center">
        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
          Turn any YouTube video into a quiz
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
          Watch a video and get quizzed on it, or skip straight to the questions. You can also paste
          or dictate your own text and we'll quiz you on that.
        </p>
      </div>

      <div className="inline-flex rounded-lg border p-1">
        <button
          type="button"
          onClick={() => setTab("youtube")}
          className={`inline-flex items-center gap-2 rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
            tab === "youtube" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <Link2 className="size-4" />
          From a video
        </button>
        <button
          type="button"
          onClick={() => setTab("text")}
          className={`inline-flex items-center gap-2 rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
            tab === "text" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <Type className="size-4" />
          From text
        </button>
      </div>

      {tab === "youtube" ? (
        <Card className="w-full max-w-2xl">
          <CardHeader>
            <CardTitle>Start from a YouTube link</CardTitle>
            <CardDescription>Paste a video URL to get started.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="youtube-url">YouTube video link</Label>
              <Input
                id="youtube-url"
                type="url"
                placeholder="https://www.youtube.com/watch?v=…"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                autoFocus
              />
            </div>
            {countsBlock}
            {error && <Alert variant="destructive">{error}</Alert>}
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                type="submit"
                size="lg"
                className="flex-1"
                onClick={(e) => submit(e, "quiz-only")}
              >
                <Zap />
                Start quiz now
              </Button>
              <Button
                type="submit"
                size="lg"
                variant="secondary"
                className="flex-1"
                onClick={(e) => submit(e, "watch")}
              >
                <PlayCircle />
                Watch first, then quiz
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="w-full max-w-2xl">
          <CardHeader>
            <CardTitle>Start from your own text</CardTitle>
            <CardDescription>
              Paste a transcript, article, or notes — or dictate with the microphone. We'll build the
              same three-level quiz.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="source-text">Your text</Label>
                {speech.supported && (
                  <Button
                    type="button"
                    size="sm"
                    variant={speech.listening ? "destructive" : "outline"}
                    onClick={() => (speech.listening ? speech.stop() : speech.start())}
                  >
                    {speech.listening ? <MicOff /> : <Mic />}
                    {speech.listening ? "Stop dictation" : "Dictate"}
                  </Button>
                )}
              </div>
              <Textarea
                id="source-text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Paste the text you want to be quizzed on…"
                className="min-h-[200px]"
              />
              {interim && <p className="text-xs italic text-muted-foreground">…{interim}</p>}
              <p
                className={`text-xs ${
                  overAccepted ? "text-destructive" : "text-muted-foreground"
                }`}
              >
                {words.toLocaleString()} words
                {textLimits.maxPaid > textLimits.maxFree
                  ? ` · free budget ${textLimits.maxFree.toLocaleString()} · paid up to ${textLimits.maxPaid.toLocaleString()}`
                  : ` · budget ${textLimits.maxFree.toLocaleString()}`}
              </p>
              {overBudget && !overAccepted && (
                <Alert>
                  Your text is longer than the {textLimits.maxFree.toLocaleString()}-word free budget. We'll
                  keep the most representative parts and quiz you on those — the rest is skipped.
                </Alert>
              )}
            </div>
            {countsBlock}
            {textError && <Alert variant="destructive">{textError}</Alert>}
            <Button
              type="submit"
              size="lg"
              className="w-full"
              disabled={textBusy || overAccepted}
              onClick={(e) => void submitText(e)}
            >
              {textBusy ? <Loader2 className="animate-spin" /> : <Zap />}
              Generate quiz from text
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="grid w-full max-w-2xl gap-4 sm:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Skip the video</CardTitle>
            <CardDescription>
              Paste a link and jump straight into questions while our pipeline processes in the
              background.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Alert>
              No context? No problem. We summarize the transcript so you can answer from memory.
            </Alert>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Bring your own text</CardTitle>
            <CardDescription>
              Articles, lecture notes, or a dictated summary — paste it in and get quizzed the same way.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Alert variant="success">No video needed. Any 50–2,000 words will do.</Alert>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
